import React from 'react';
import BuyDirect from '../BuyDirect';
import ComingSoonCostInformation from '../ComingSoonCostInformation';
import { ProcessedAuction } from '~/App/shared/interfaces/ProcessedAuction';
import {
  getCarDealerPrice,
  isComingSoonAuction,
  isRecreationalVehicle as isRecreationalVehicleHelper,
  ProcessedAuctionStates
} from '~/helpers/auction';
import styled from 'styled-components';
import { mq } from '@kvdbil/components';
import EndingTimer from './EndingTimer';
import BuyMethodTitle from './BuyMethodTitle';
import BuyMethodButton from './BuyMethodButton';
import AuctionInfo from '../AuctionInfo';
import TooltipPrice from '~/App/shared/components/TooltipPrice';
import { useTranslation } from '~/Locale';
import AuctionStatusForBuyMethod from './AuctionStatusForBuyMethod';
import { AuctionType } from '~/App/shared/interfaces/Auction';
import { isHeavyEquipmentObject } from '../../helpers';

interface BuyMethodContainerProps {
  buyMethod?: AuctionType;
}

export const BuyMethodContainer = styled.div<BuyMethodContainerProps>`
  display: flex;
  flex-direction: column;
  border-radius: 0.5rem;

  ${({ theme, buyMethod }) =>
    buyMethod === 'BUY_NOW' &&
    `background-color: ${theme.colors.info.lighter3}`};
  ${({ theme, buyMethod }) =>
    buyMethod === 'BIDDING' &&
    `background-color: ${theme.colors.secondary.lighter3}`};
  ${({ theme, buyMethod }) =>
    !buyMethod && `background-color: ${theme.colors.background.lightGray}`};

  padding: 2rem 3.5rem;
  flex-grow: 1;
  width: 100%;

  &:only-child {
    max-width: 548px;
    margin: 0 auto;
  }

  ${mq('tablet', 'laptop')} {
    padding: 2rem;
  }

  ${mq(null, 'tablet')} {
    padding: 2rem 1rem;
  }
`;

const BuyMethodFooterContainer = styled.div`
  margin-top: auto;
`;

interface Props {
  auction: ProcessedAuction;
  buyMethod: AuctionType;
  auctionStates: ProcessedAuctionStates;
  reservationPriceReached: boolean;
}

const BuyMethodBox = ({
  auction,
  buyMethod,
  auctionStates,
  reservationPriceReached
}: Props) => {
  const { t } = useTranslation();
  const {
    isClosed,
    isWinner,
    isParticipating,
    isLeading,
    isOverbidded,
    isSoldByBuyNow
  } = auctionStates;
  const carDealerPrice = getCarDealerPrice(auction);
  const isRecreationalVehicle = isRecreationalVehicleHelper(
    auction?.processObject?.objectType ?? ''
  );

  const isComingSoon = isComingSoonAuction(auction);
  const isHeavyEquipment = isHeavyEquipmentObject(auction.processObject);

  if (isComingSoon) {
    return (
      <ComingSoonCostInformation
        auction={auction}
        auctionStates={auctionStates}
        isRecreationalVehicle={isRecreationalVehicle}
      />
    );
  }

  const closeAt =
    buyMethod === AuctionType.BUY_NOW
      ? auction?.buyNowUntil
      : auction?.activeAuction?.preliminaryCloseAt;

  return (
    <BuyMethodContainer buyMethod={buyMethod}>
      <BuyMethodTitle buyMethod={buyMethod} />
      {buyMethod !== AuctionType.BUY_NOW && (
        <AuctionInfo
          auction={auction}
          auctionStates={auctionStates}
          isComingSoon={isComingSoon}
          isRecreationalVehicle={isRecreationalVehicle}
          isHeavyEquipment={isHeavyEquipment}
        />
      )}
      {buyMethod === AuctionType.BUY_NOW && (
        <BuyDirect
          auction={auction}
          isRecreationalVehicle={isRecreationalVehicle}
        />
      )}
      {carDealerPrice && !isHeavyEquipment && (
        <TooltipPrice
          price={carDealerPrice}
          title={t('Car dealer price')}
          explanation={t('Car dealer price explanation')}
        />
      )}

      {(isClosed || isOverbidded || isLeading) && (
        <AuctionStatusForBuyMethod
          isWinner={isWinner}
          isParticipating={isParticipating}
          isLeading={isLeading}
          isClosed={isClosed}
          reservationPriceReached={reservationPriceReached}
          isSoldByBuyNow={isSoldByBuyNow}
          buyMethod={buyMethod}
        />
      )}

      <BuyMethodFooterContainer>
        <BuyMethodButton
          auction={auction}
          buyMethod={buyMethod}
          auctionStates={auctionStates}
        />

        {closeAt && (
          <EndingTimer
            buyMethod={buyMethod}
            activeAuction={auction.activeAuction}
            auctionStates={auctionStates}
            closeAt={closeAt}
          />
        )}
      </BuyMethodFooterContainer>
    </BuyMethodContainer>
  );
};

export default BuyMethodBox;
